import React, { useRef, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import PropTypes from 'prop-types';
import OrderedRow from './OrderedRow';
import { moveRows } from './circle_helpers';

export default function OrderedRows({
  rows,
  onChange,
  onRemove,
  renderRow,
  disabled,
  header,
  renderDragPreview,
  emptyMessage = 'No Circles or creators added yet.'
}) {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [moving, setMoving] = useState([]);
  const anchor = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const selection = selected.filter(id => rows.some(row => row.id === id));
  const cancel = () => {
    setSelecting(false);
    setSelected([]);
    anchor.current = null;
  };
  const select = (id, event) => {
    const add = event.target.checked;
    const ids =
      event.nativeEvent.shiftKey && anchor.current !== null
        ? rows
            .slice(
              Math.min(
                anchor.current,
                rows.findIndex(row => row.id === id)
              ),
              Math.max(
                anchor.current,
                rows.findIndex(row => row.id === id)
              ) + 1
            )
            .map(row => row.id)
        : [id];
    setSelected(add ? [...new Set([...selection, ...ids])] : selection.filter(id => !ids.includes(id)));
    anchor.current = rows.findIndex(row => row.id === id);
  };
  return (
    <div className='circle-ordered-rows'>
      <div className='circle-toolbar'>
        <button type='button' disabled={disabled} onClick={() => (selecting ? cancel() : setSelecting(true))}>
          {selecting ? 'Cancel' : 'Select'}
        </button>
        {selecting && (
          <label className='circle-check'>
            <input
              type='checkbox'
              disabled={disabled}
              aria-label='Select all rows'
              checked={!!rows.length && selection.length === rows.length}
              ref={node => {
                if (node) node.indeterminate = !!selection.length && selection.length < rows.length;
              }}
              onChange={event => setSelected(event.target.checked ? rows.map(row => row.id) : [])}
            />
            Select all
          </label>
        )}
        <span aria-live='polite'>
          {selection.length ? `${selection.length} selected` : selecting ? 'Select rows to move or remove' : 'Drag a handle to reorder'}
        </span>
        {selecting && !!selection.length && (
          <button
            type='button'
            disabled={disabled}
            onClick={() => {
              onRemove(selection);
              setSelected([]);
            }}
          >
            Remove selected ({selection.length})
          </button>
        )}
      </div>
      {header}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => {
          const ids = selecting && selection.includes(active.id) ? selection : [active.id];
          setMoving(ids);
          if (selecting) setSelected(ids);
        }}
        onDragCancel={() => setMoving([])}
        onDragEnd={({ active, over }) => {
          if (over) onChange(moveRows(rows, moving, active.id, over.id));
          setMoving([]);
        }}
      >
        <SortableContext items={rows.map(row => row.id)} strategy={verticalListSortingStrategy}>
          {rows.map((row, index) => (
            <OrderedRow
              key={row.id}
              row={row}
              index={index}
              disabled={disabled}
              selecting={selecting}
              selected={selection.includes(row.id)}
              moving={moving.includes(row.id)}
              onSelect={event => select(row.id, event)}
            >
              {renderRow(row, index)}
            </OrderedRow>
          ))}
        </SortableContext>
        <DragOverlay>
          {!!moving.length && <div className='circle-drag-overlay'>{renderDragPreview(rows.filter(row => moving.includes(row.id)))}</div>}
        </DragOverlay>
      </DndContext>
      {!rows.length && <p>{emptyMessage}</p>}
    </div>
  );
}

OrderedRows.propTypes = {
  rows: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  renderRow: PropTypes.func.isRequired,
  header: PropTypes.node,
  emptyMessage: PropTypes.string,
  renderDragPreview: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};
